import type { Route } from '@/services/router/routes';
import type { ProductTaxonomy } from '@/services/product-taxonomy';
import type { RegisteredRouter } from '@tanstack/react-router';

type RegisteredRoutePath = keyof RegisteredRouter['routesByPath'];
type RegisteredRoutePathWithSearch = `${RegisteredRoutePath}?${string}`;

/**
 * Human-readable labels for command palette navigation search.
 * Labels match what users see in breadcrumbs and the UI nav.
 * Routes not listed here fall back to auto-generated labels from path segments.
 * Update this file when adding or renaming pages.
 *
 * Static labels whose route key and text never change. Routes whose label and/or URL
 * segment are renamed per-account by the Journeys taxonomy live in `getNavigationLabels`
 * below, not here. Prefer `getNavigationLabels(taxonomy)` over reading this directly.
 */
const HYDRA_STATIC_NAVIGATION_LABELS = Object.freeze({
  // ── Home ──────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/home': 'Home',
  '/workspaces/$workspaceId/home/add-people': 'Home / Add People',

  // ── Agent ─────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/agent': 'Agent',
  '/workspaces/$workspaceId/agent/routines': 'Agent / Routines',
  '/workspaces/$workspaceId/agent/skills': 'Agent / Skills',

  // ── Health ────────────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/health': 'Workspace Health',
  '/workspaces/$workspaceId/health?tab=services': 'Workspace Health / Services',
  '/workspaces/$workspaceId/health?tab=data': 'Workspace Health / Data',
  '/workspaces/$workspaceId/health?tab=messaging': 'Workspace Health / Messaging',

  // ── Analysis ─────────────────────────────────────────────────────────────
  // The Deliverability and Email clients entries are taxonomy-gated — see getNavigationLabels below.

  // ── Send messages ─────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/journeys/message-library': 'Message library',

  // ── Create content ────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/assets': 'Assets',
  '/workspaces/$workspaceId/snippets': 'Snippets',

  // ── Configure data ────────────────────────────────────────────────────────
  '/workspaces/$workspaceId/journeys/exports': 'Exports',
  '/workspaces/$workspaceId/journeys/collections': 'Collections',
  // The migrated URL: correct in both flag states, since flag-off it forwards to the `journeys`
  // twin the embedded app serves.
  '/workspaces/$workspaceId/integrations/reporting-webhooks/': 'Integrations / Reporting Webhooks',
  // Same reasoning for the three warehouse connector pages (FPS-2698). These replace a single
  // `journeys/integrations/warehouse` entry that pointed at a URL which has never rendered
  // anything: Ember declares that segment with child routes and no index template, so the palette
  // led to a blank page. Each connector is a real page, and keeping "Warehouse" in the label keeps
  // the search term working.
  '/workspaces/$workspaceId/integrations/warehouse/redshift': 'Integrations / Warehouse / Amazon Redshift',
  '/workspaces/$workspaceId/integrations/warehouse/bigquery': 'Integrations / Warehouse / Google BigQuery',
  '/workspaces/$workspaceId/integrations/warehouse/snowflake': 'Integrations / Warehouse / Snowflake',

  // ── Pipelines (CDP) ───────────────────────────────────────────────────────
  '/workspaces/$workspaceId/pipelines/': 'Pipelines',
  '/workspaces/$workspaceId/pipelines/sources/': 'Pipelines / Sources',
  '/workspaces/$workspaceId/pipelines/sources/journeys': 'Pipelines / Sources / Journeys',
  '/workspaces/$workspaceId/pipelines/destinations/': 'Pipelines / Destinations',
  '/workspaces/$workspaceId/pipelines/destinations/journeys': 'Pipelines / Destinations / Journeys',

  // ── Workspace settings (Hydra-owned) ──────────────────────────────────────
  '/workspaces/$workspaceId/settings/': 'Workspace settings',
  '/workspaces/$workspaceId/settings/ai-business-profile': 'Workspace settings / Business context',
  '/workspaces/$workspaceId/settings/audit-logs': 'Workspace settings / Audit logs',
  '/workspaces/$workspaceId/settings/api-keys': 'Workspace settings / API keys',
  '/workspaces/$workspaceId/settings/edit': 'Workspace settings / General',
  '/workspaces/$workspaceId/settings/geofences': 'Workspace settings / Geofences',
  '/workspaces/$workspaceId/settings/api_credentials/': 'Workspace settings / API credentials',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=tracking':
    'Workspace settings / API credentials / Tracking API keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=app':
    'Workspace settings / API credentials / App API keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=signing':
    'Workspace settings / API credentials / Webhook signing keys',
  '/workspaces/$workspaceId/settings/api_credentials/?keyType=service_accounts':
    'Workspace settings / API credentials / Service accounts',
  '/workspaces/$workspaceId/settings/language': 'Workspace settings / Language',
  '/workspaces/$workspaceId/settings/objects/': 'Workspace settings / Custom Object types',
  '/workspaces/$workspaceId/settings/merge_options': 'Workspace settings / Merge options',
  '/workspaces/$workspaceId/settings/llm_configuration/': 'Workspace settings / LLM Configuration',
  '/workspaces/$workspaceId/settings/suppressions': 'Workspace settings / Suppressions',
  '/workspaces/$workspaceId/settings/webhook_configuration/': 'Workspace settings / Webhooks',
  '/workspaces/$workspaceId/settings/actions/sms-providers': 'Workspace settings / Custom SMS',
  // The email tabs are TSR-owned now (FPS-2349); `/settings/actions/email/account_verification`
  // is not part of that migration and stays in the manual map below.
  '/workspaces/$workspaceId/settings/actions/email': 'Workspace settings / Email',
  '/workspaces/$workspaceId/settings/actions/email/sending_domains/': 'Workspace settings / Email / Sending domains',
  '/workspaces/$workspaceId/settings/actions/email/dynamic_addresses': 'Workspace settings / Email / Dynamic addresses',
  '/workspaces/$workspaceId/settings/actions/email/smtp': 'Workspace settings / Email / SMTP',
  '/workspaces/$workspaceId/settings/actions/email/suppression_list': 'Workspace settings / Email / Suppression list',
  '/workspaces/$workspaceId/settings/actions/email/defaults': 'Workspace settings / Email / Defaults',

  // ── Account settings ──────────────────────────────────────────────────────
  '/settings/': 'Account Settings',
  '/settings/audit-logs': 'Account Settings / Audit Logs',
  // Billing is TSR-owned now, and every tab has landed, so all of them are listed here.
  '/settings/billing': 'Account Settings / Billing',
  '/settings/billing/your-plan': 'Account Settings / Billing / Your Plan',
  '/settings/billing/billing-history': 'Account Settings / Billing / Billing History',
  '/settings/billing/available-plans/journeys': 'Account Settings / Billing / Available Plans',
  '/settings/billing/payment-methods': 'Account Settings / Billing / Payment Methods',
  '/settings/billing/checkout': 'Account Settings / Billing / Checkout',
  '/settings/billing/llm-usage': 'Account Settings / Billing / AI usage',
  '/settings/billing/inbox-preview-usage': 'Account Settings / Billing / Inbox preview usage',
  '/settings/billing/profile-growth': 'Account Settings / Billing / Profile growth',
  '/settings/receipts': 'Account Settings / Receipts',
  '/settings/subscription': 'Account Settings / Subscription',
  '/settings/account/': 'Account Settings / Account',
  '/settings/roles/': 'Account Settings / Roles',
  '/settings/security/': 'Account Settings / Security',
  '/settings/security/sso': 'Account Settings / Security / SSO',
  '/settings/privacy': 'Account Settings / Privacy',
  '/settings/ai': 'Account Settings / AI & MCP',
  '/settings/workspaces/': 'Account Settings / Workspaces',
  '/settings/api_credentials/': 'Account Settings / API Credentials',
  '/settings/api-keys': 'Account Settings / API Keys',
  '/settings/experimental-features': 'Account Settings / Experimental Features',
  // The personal-settings index is TSR-owned now (FPS-2861); `/settings/personal/security`
  // stays in the manual map below until it migrates.
  '/settings/personal/': 'Personal Settings',
} satisfies Partial<Record<RegisteredRoutePath | RegisteredRoutePathWithSearch, string>>);

const CHILD_APP_STATIC_NAVIGATION_LABELS = Object.freeze({
  // ── Design Studio ─────────────────────────────────────────────────────────
  // `/design-studio/dashboard` (CON-7495) and `/design-studio/global-styles`
  // (CON-7588) are TanStack routes now, so their entries live in
  // getNavigationLabels below, not this typed child-app map (a TSR route key
  // can't type this manual map).

  // ── Send messages ─────────────────────────────────────────────────────────
  // Note: the Campaigns/Automations and Newsletters/One-time sends entries are
  // taxonomy-gated — see getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/broadcasts': 'Broadcasts',
  '/workspaces/:workspaceId/journeys/broadcasts/api-triggered': 'Broadcasts / API-triggered',
  '/workspaces/:workspaceId/journeys/transactional': 'Transactional',
  '/workspaces/:workspaceId/journeys/anonymous': 'Anonymous',

  // ── Create content ────────────────────────────────────────────────────────
  // `/journeys/layouts` (list) and `/journeys/layouts/starters` are Hydra/TanStack
  // routes now, so both live in getNavigationLabels below, not this typed
  // child-app map (a TSR route key can't type this manual map).

  // ── Manage audience ───────────────────────────────────────────────────────
  // Note: the People/Profiles entry is taxonomy-gated — see getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/segments': 'Segments',
  '/workspaces/:workspaceId/journeys/segments/explore': 'Segment Explorer',
  '/workspaces/:workspaceId/journeys/goals': 'Goals',
  '/workspaces/:workspaceId/journeys/objects': 'Custom objects',

  // ── Review ────────────────────────────────────────────────────────────────
  // Note: the Analysis/Reports, Activity Logs/Profile activity, and Deliveries & Drafts/Message
  // activity entries are taxonomy-gated — see getNavigationLabels below.

  // ── Configure data ────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/journeys/data-index': 'Data index',
  // `journeys/integrations/warehouse` used to sit here. It renders nothing — see the three
  // connector entries in the Hydra map above, which replaced it.
  '/workspaces/:workspaceId/journeys/imports': 'Imports',

  // ── Pipelines (CDP) ───────────────────────────────────────────────────────
  '/workspaces/:workspaceId/pipelines/sources/reverse-etl': 'Pipelines / Sources / Reverse ETL',
  '/workspaces/:workspaceId/pipelines/connections': 'Pipelines / Connections',
  '/workspaces/:workspaceId/pipelines/catalog': 'Pipelines / Catalog',

  // ── Workspace settings ────────────────────────────────────────────────────
  '/workspaces/:workspaceId/settings/subscription_center': 'Workspace settings / Subscription Center',
  '/workspaces/:workspaceId/settings/subscription_center/branding/edit':
    'Workspace settings / Subscription Center / Branding',
  '/workspaces/:workspaceId/settings/subscription_center/settings':
    'Workspace settings / Subscription Center / Settings',
  '/workspaces/:workspaceId/settings/subscription_center/localization/edit':
    'Workspace settings / Subscription Center / Localization',
  // ── Workspace settings / channel actions ──────────────────────────────────
  '/workspaces/:workspaceId/settings/actions/email/account_verification':
    'Workspace settings / Email / Account verification',
  '/workspaces/:workspaceId/settings/actions/sms': 'Workspace settings / SMS',
  '/workspaces/:workspaceId/settings/actions/push': 'Workspace settings / Push',
  '/workspaces/:workspaceId/settings/actions/in_app': 'Workspace settings / In-App',
  '/workspaces/:workspaceId/settings/actions/slack': 'Workspace settings / Slack',
  '/workspaces/:workspaceId/settings/actions/twilio': 'Workspace settings / Twilio SMS',
  '/workspaces/:workspaceId/settings/actions/whatsapp': 'Workspace settings / WhatsApp',
  '/workspaces/:workspaceId/settings/actions/line': 'Workspace settings / LINE',
  '/workspaces/:workspaceId/settings/actions/messaging_limit': 'Workspace settings / Messaging Limit',
  '/workspaces/:workspaceId/settings/actions/time_zone_match': 'Workspace settings / Time Zone Matching',
  '/workspaces/:workspaceId/settings/actions/url_params': 'Workspace settings / URL Parameters',

  // ── Account settings ──────────────────────────────────────────────────────
  // The `/settings/billing` shell and the `your-plan`, `billing-history`, `available-plans`, and
  // `payment-methods` tabs are TSR-owned; their labels live in HYDRA_STATIC_NAVIGATION_LABELS above.
  // The remaining tab sub-paths aren't migrated yet, so the embedded app still serves them (flag off)
  // and they stay searchable here until each tab lands.
  // `/settings/security` and its `/sso` page are TSR-owned now (FPS-2223 / FPS-2737); their labels
  // live in HYDRA_STATIC_NAVIGATION_LABELS above. `/settings/personal` (the index) is TSR-owned as
  // of FPS-2861 and moved there too; only its still-Ember `/security` sub-path remains here.
  '/settings/personal/security': 'Personal Settings / Security',
} satisfies Partial<Record<Route, string>>);

const STATIC_NAVIGATION_LABELS = Object.freeze({
  ...HYDRA_STATIC_NAVIGATION_LABELS,
  ...CHILD_APP_STATIC_NAVIGATION_LABELS,
});

/**
 * Full route→label map for command palette navigation, including routes whose label
 * and/or URL segment are renamed per-account by the Journeys taxonomy (resolved via the
 * passed taxonomy). This is the single source of truth for navigable route definitions —
 * add future renamed routes to the taxonomy-gated block below, keyed by `taxonomy.route(…)`.
 */
export function getNavigationLabels(taxonomy: ProductTaxonomy): Record<string, string> {
  return {
    ...STATIC_NAVIGATION_LABELS,

    // Migrated Hydra routes served at their canonical URLs. Kept here (untyped
    // map) rather than STATIC_NAVIGATION_LABELS because their typed route keys
    // don't reliably resolve as `KnownRoute` under CI's typechecker.
    '/workspaces/:workspaceId/journeys/layouts': 'Email layouts',
    '/workspaces/:workspaceId/journeys/layouts/starters': 'Email layouts / Starters',
    '/workspaces/:workspaceId/design-studio/dashboard': 'Design Studio',
    '/workspaces/:workspaceId/design-studio/global-styles': 'Design Studio / Global Styles',
    '/workspaces/:workspaceId/journeys/integrations': 'Integrations',

    // ── Taxonomy-gated (Journeys taxonomy rename) ─────────────────────────────
    // Both the route key and the label depend on the active taxonomy variant.
    // Review › Analysis → Reports, including its two Hydra-owned tabs, whose URL segment
    // renames from `/analysis` to `/reports` with the same flag.
    [taxonomy.route('analysis.index')]: taxonomy.t('nav.review.analysis'),
    [taxonomy.route('analysis.deliverability')]: 'Deliverability Analysis',
    [taxonomy.route('analysis.emailClients')]: 'Email Clients Analysis',
    // Review › Activity Logs → Profile activity
    [taxonomy.route('profileActivity.index')]: taxonomy.t('nav.review.activityLogs'),
    // Manage audience › People → Profiles
    [taxonomy.route('people.index')]: taxonomy.t('nav.manageAudience.people'),
    // Send messages › Campaigns → Automations
    [taxonomy.route('campaigns.index')]: taxonomy.t('nav.sendMessages.campaigns'),
    // Send messages › Newsletters → One-time sends
    [taxonomy.route('broadcasts.oneTimeSends')]: `Broadcasts / ${taxonomy.t('nav.broadcasts.newsletters')}`,
    // Review › Deliveries & Drafts → Message activity, with the Deliveries tab → Sent (Drafts
    // unchanged). Flag-off these resolve to the /outbox, /outbox/deliveries, /outbox/drafts URLs;
    // flag-on to /message_activity, /message_activity/sent, /message_activity/drafts.
    [taxonomy.route('messageActivity.index')]: taxonomy.t('nav.review.outbox'),
    [taxonomy.route('messageActivity.sent')]: taxonomy.t('nav.review.outbox.deliveries'),
    [taxonomy.route('messageActivity.drafts')]: taxonomy.t('nav.review.outbox.drafts'),
  };
}
